import isEqual from 'lodash/isEqual'

import {
  COMMUNITY_DEFAULT_DATE_RANGE,
  INSIGHTS_DEFAULT_DATE_RANGE,
  isCommunityCanonicalDateRange,
} from './captureFilters'
import type { SavedViewLike, SavedViewSection } from './types'

/**
 * Filter keys whose array values represent an unordered set — saved views
 * storing `['Y', 'X']` should still match a current URL declaring those ids
 * in the opposite order. Ordering is not user-visible for these keys, so
 * comparison sorts a clone before deep-equal.
 *
 * These are the *canonical* (post-`captureFilters`) keys.
 */
const ORDER_INSENSITIVE_ARRAY_KEYS: readonly string[] = [
  'channelIds',
  'tagIds',
  'groupIds',
]

function isUnknownArray(value: unknown): value is readonly unknown[] {
  return Array.isArray(value)
}

function sortedClone(value: readonly unknown[]): unknown[] {
  // Stringify non-string entries so the comparator stays total across the
  // heterogeneous `unknown[]` shape we get from a `FilterJSON` payload.
  return [...value].toSorted((a, b) => String(a).localeCompare(String(b)))
}

function normalizeFilters(
  section: SavedViewSection,
  filters: Record<string, unknown>,
): Record<string, unknown> {
  const normalized: Record<string, unknown> = { ...filters }
  for (const key of ORDER_INSENSITIVE_ARRAY_KEYS) {
    const value = normalized[key]
    if (isUnknownArray(value)) {
      normalized[key] = sortedClone(value)
    }
  }
  // A view saved before the default date range was captured (legacy Insights
  // payload with no `dateRange`) still represents the last-30-days view, which
  // is what capture now produces. Apply the same default to both sides so the
  // stored view keeps matching its route instead of orphaning into a duplicate.
  if (section === 'insights' && normalized.dateRange === undefined) {
    normalized.dateRange = INSIGHTS_DEFAULT_DATE_RANGE
  }
  // A stored range we no longer offer opens on the default, so match it on the
  // default too instead of orphaning the view into a duplicate.
  if (
    section === 'community' &&
    normalized.dateRange !== undefined &&
    !isCommunityCanonicalDateRange(normalized.dateRange)
  ) {
    normalized.dateRange = COMMUNITY_DEFAULT_DATE_RANGE
  }
  return normalized
}

/**
 * Finds the saved view that exactly matches the current route + filters
 * context. `view` is normalized to `null` so `Maybe<string>` from the query
 * compares equal to `string | null` from `deriveViewFromRoute`.
 *
 * Array equality ignores order for keys whose values are user-perceived as
 * sets (`channels`, `tags`, `tagIds`, `groupIds`) — a URL declaring
 * `?channels[]=Y&channels[]=X` still matches a saved view storing
 * `['X', 'Y']`. Other array-valued filter keys remain order-sensitive.
 *
 * Shared between the page-header `SaveViewButton` and the sidebar
 * `SavedViewItem` — both must agree on what "the active saved view" means.
 */
export function findMatchingSavedView<T extends SavedViewLike>(
  savedViews: readonly T[],
  section: SavedViewSection,
  view: string | null,
  filters: Record<string, unknown>,
): T | undefined {
  const normalizedCurrent = normalizeFilters(section, filters)
  return savedViews.find(
    (sv) =>
      sv.section === section &&
      (sv.view ?? null) === view &&
      isEqual(normalizeFilters(sv.section, sv.filters), normalizedCurrent),
  )
}
